import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash.isempty';
import { startOfDay, endOfDay, subDays } from 'date-fns';
import { Link } from 'react-router-dom';

import {
	loadCareQuestDashboard,
	acknowledge,
	reply,
	loadResponses,
	loadQuestions,
	loadRelatedResponses,
	loadVitalTrackerDashboard,
	acknowledgeVitalNotification,
	replyVitalNotification,
} from './api';
import ReplyModal from './ReplyModal';
import CareQuestPanel from './CareQuestPanel';
import RPMPanel from './RPMPanel';
import RPMDataModal from './RPMDataModal';
import {
	Button,
	DateRangePickerDropdown,
	TabPanel,
	Tab,
} from '../../../components';

export default class PatientNotifications extends React.Component {
	static propTypes = {
		user: PropTypes.object.isRequired,
	};

	static defaultProps = {
		user: {},
	};

	constructor(props) {
		super(props);
		this.state = {
			isLoading: false,
			data: [],
			showModal: false,
			rpmData: [],
			showRPMModal: false,
			startDate: subDays(startOfDay(new Date()), 7),
			endDate: endOfDay(new Date()),
		};
	}

	componentDidMount() {
		this.loadDashboard();
	}

	loadDashboard = async () => {
		const { startDate, endDate } = this.state;
		const user = this.props.user.toJSON();
		this.setState({ isLoading: true, isError: false });
		try {
			const data = await loadCareQuestDashboard(user.provider_practice_id);
			const { data: rpmData } = await loadVitalTrackerDashboard(
				user.id,
				startDate,
				endDate
			);
			this.setState({
				isLoading: false,
				data,
				rpmData,
			});
		} catch (err) {
			this.setState({ isLoading: false, isError: true });
		}
	};

	handleAcknowledge = (groupId) => {
		acknowledge({ id: groupId, providerId: this.props.user.get('id') }).then(
			() => this.loadDashboard()
		);
	};

	handleAcknowledgeVital = (vital_id) => {
		this.setState({ showRPMModal: false });
		acknowledgeVitalNotification({
			providerId: this.props.user.get('id'),
			vital_id,
		}).then(() => this.loadDashboard());
	};

	handleReply = (response) => {
		if (response.vital_id) {
			this.setState({
				showModal: true,
				selectedResponse: response,
			});
		} else {
			Promise.all([
				loadResponses(response.care_quest_response_groupId),
				loadQuestions(response.care_quest_groupId),
				loadRelatedResponses(
					response.care_quest_groupId,
					response.patientId,
					response.care_quest_response_groupId
				),
			]).then(([responses, questions, relatedResponses]) => {
				this.setState({
					showModal: true,
					selectedResponse: response,
					responses: responses.data,
					questions: questions.data,
					relatedResponses: relatedResponses.data,
				});
			});
		}
	};

	handleCancel = () => {
		this.setState({
			showModal: false,
			selectedResponse: null,
		});
	};

	handleSaveReply = (response) => {
		if (response.vital_id) {
			replyVitalNotification({
				providerId: this.props.user.get('id'),
				...response,
			}).then(() => {
				this.setState({
					showModal: false,
					showRPMModal: false,
					selectedResponse: null,
				});
				this.loadDashboard();
			});
		} else {
			reply({ providerId: this.props.user.get('id'), ...response }).then(() => {
				this.setState({
					showModal: false,
					selectedResponse: null,
				});
				this.loadDashboard();
			});
		}
	};

	displayValueWithUnit = ({ value, unit }) => {
		const start = unit.indexOf('{');
		const end = unit.indexOf('}');
		return unit.replace(unit.substring(start, end + 1), value);
	};

	handleCloseRPMModal = () => {
		this.setState({ showRPMModal: false });
	};

	handleShowRPMModal = (selectedRPMData) => {
		this.setState({
			showRPMModal: true,
			selectedRPMData,
		});
	};

	handleDateRangeChosen = ({ startDate, endDate }) => {
		this.setState(
			() => ({
				startDate,
				endDate,
			}),
			this.loadDashboard
		);
	};

	render() {
		const user = this.props.user.toJSON();
		const {
			isLoading,
			data,
			showModal,
			selectedResponse,
			responses,
			questions,
			relatedResponses,
			rpmData,
			showRPMModal,
			selectedRPMData,
			startDate,
			endDate,
		} = this.state;

		const noPolling = !!user.provider_practice.no_polling_ind;

		return (
			<div className="content-wrapper patient-notifications">
				<ReplyModal
					isOpen={showModal}
					onCancel={this.handleCancel}
					onSave={this.handleSaveReply}
					responseGroup={selectedResponse}
					responses={responses}
					questions={questions}
					relatedResponses={relatedResponses}
				/>
				<RPMDataModal
					isOpen={showRPMModal}
					onCancel={this.handleCloseRPMModal}
					rpmData={selectedRPMData}
					providerId={user.id}
					onReply={this.handleSaveReply}
					onAcknowledge={this.handleAcknowledgeVital}
				/>
				<ol className="breadcrumb">
					<li>
						<Link to="/">Home</Link>
					</li>
					<li>Patient Notifications</li>
					<li className="active">New</li>
				</ol>
				<div className="row pb">
					<div className="col-md-12 text-right">
						{noPolling && (
							<Button
								disabled={isLoading}
								icon="refresh"
								altTitle="Refresh Patient Notifications"
								onClick={this.loadDashboard}
								className="mr"
							/>
						)}
						<DateRangePickerDropdown
							maxDate={new Date()}
							onRangeChosen={this.handleDateRangeChosen}
							startDate={startDate}
							endDate={endDate}
							position="right"
						/>
					</div>
				</div>
				<TabPanel>
					<Tab
						id="questionnaire"
						title={`Questionnaire ${
							Array.isArray(data) && data.length > 0 ? `(${data.length})` : ''
						}`}
					>
						{isLoading && <span>Loading...</span>}
						{!isLoading && data.length == 0 && <div>NO RECORDS FOUND</div>}
						{!isLoading &&
							data.map((response) => (
								<CareQuestPanel
									key={response.care_quest_response_groupId}
									response={response}
									onReply={this.handleReply}
									onAcknowledge={this.handleAcknowledge}
								/>
							))}
					</Tab>
					<Tab
						id="rpm"
						title={`RPM ${
							Array.isArray(rpmData) && rpmData.length > 0
								? `(${rpmData.length})`
								: ''
						}`}
					>
						{isLoading && <span>Loading...</span>}
						{!isLoading && rpmData.length == 0 && <div>NO RECORDS FOUND</div>}
						{!isLoading &&
							!isEmpty(rpmData) &&
							rpmData.map((rpmRecord) => (
								<RPMPanel
									key={`${rpmRecord.vital_date}:${rpmRecord.patientId}:${rpmRecord.vital_name}`}
									rpmData={rpmRecord}
									onViewData={this.handleShowRPMModal}
								/>
							))}
					</Tab>
				</TabPanel>
			</div>
		);
	}
}
